import React, { FC, useRef, useState, useMemo, useEffect, CSSProperties } from 'react';
import _ from 'lodash';
import { JitsiMeeting } from '@jitsi/react-sdk';
import { type IJitsiMeetExternalApi } from '@jitsi/react-sdk/lib/types/index';
import { useAppSelector } from 'careand-redux/store';
import PatientUtils from 'services/PatientUtils';
import moment from 'moment-timezone';
import StaffUtils from 'services/StaffUtils';
import {
  AppointmentTranscription,
  AppointmentTranscriptionMessageFromEnum,
  JitsiMeetingPayload,
  Patient,
  Physician,
} from 'generated/api';
import Icon from 'components/Utils/NewIcon';
import MiscUtils from 'services/MiscUtils';
import shutterSound from 'assets/audio/shutter.mp3';
import CalendarUtils from 'services/CalendarUtils';
import DocumentUtils from 'services/DocumentUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import {
  AppointmentTranscriptionWithPartialIndicators,
  JitsiIFrameTranscriptionService,
} from 'services/DictationService/AWSTranscribeService';
import { JitsiIFrameDeepgramTranscriptionService } from 'services/DictationService/DeepgramTranscribeService';
import {
  getTranscriptionProviderSetting,
  TranscriptionProviders,
} from 'services/DictationService/TranscriptionRouting';
import { WindowEventAudioService } from 'services/DictationService/MicService';
import { getDeepgramKeyterms } from 'services/DictationService/keyterms';
import {
  TelemedicineTranscriptionService,
  TranscriptionProvider,
  TRANSCRIPTION_STATUS,
} from 'services/DictationService/TelemedicineTranscriptionService';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultChip from '../../Utils/DefaultChip';

const RECORDING_METADATA = {
  mime: 'image/png',
  extension: 'png',
  description: 'Telemedicine Photo',
};

const JitsiMeet: FC<{
  saveTo: string;
  meetingData: JitsiMeetingPayload;
  isPhysician: boolean;
  defaults: { video: boolean; audio: boolean };
  onDisconnected: () => void;
  onConnected: (nParticipants: number) => void;
  appointmentId?: string;
}> = ({
  saveTo,
  meetingData,
  isPhysician,
  defaults,
  onDisconnected,
  onConnected,
  appointmentId,
}) => {
  const dispatch = useAppDispatch();
  const authentication = useAppSelector((state) => state.authentication);
  const user = authentication.user;

  const {
    transcription,
    setTranscription,
    transcriptionStatus,
    setTranscriptionStatus,
    hasTranscriptionPermission,
    setHasTranscriptionPermission,
    showTranscription,
    setShowTranscription,
    canUseTranscription,
  } = useTranscriptionState({
    appointmentId,
    patientId: saveTo,
    isPhysician,
  });

  const [credentialError, setCredentialError] = useState<string | null>(null);
  const [retryCounter, setRetryCounter] = useState(0);

  const telemedicineTranscriptionService = useMemo(() => {
    if (!appointmentId || !isPhysician || !hasTranscriptionPermission) {
      return;
    }

    setCredentialError(null);

    const buildService = async () => {
      const providerSetting = getTranscriptionProviderSetting();
      // Jitsi iframe audio is the merged local+remote stream: one speaker per
      // channel, so Deepgram multichannel mirrors AWS channel identification
      const deepgramOptions = {
        channels: 2 as const,
        multichannel: true,
        keyterms: getDeepgramKeyterms(),
      };

      let transcriptionService: TranscriptionProvider;
      let builtService: TelemedicineTranscriptionService | undefined;

      // Mid-session provider death (e.g. max reconnects) must surface as an
      // error state, not a silently frozen transcript.
      const onProviderError = (err: any) => {
        builtService?.stop().catch(() => {});
        setCredentialError(err?.message || 'Transcription error');
        setTranscriptionStatus(TRANSCRIPTION_STATUS.error);
        MiscUtils.showToastrError('Transcription', err?.message ?? 'Unknown Error');
      };

      if (providerSetting === TranscriptionProviders.Deepgram) {
        // Preflight the token so misconfiguration lands in the catch below
        // (toast + error status + retry), not as a silent start-time failure.
        const token = await JitsiIFrameDeepgramTranscriptionService.getCredentials();
        if (!token?.access_token) {
          throw new Error('Failed to get Deepgram credentials');
        }
        transcriptionService = new JitsiIFrameDeepgramTranscriptionService(
          new WindowEventAudioService(),
          undefined,
          onProviderError,
          deepgramOptions,
        );
      } else {
        const credentials = await JitsiIFrameTranscriptionService.getCredentials();
        if (!credentials) {
          throw new Error('Failed to get AWS Credentials');
        }
        transcriptionService = new JitsiIFrameTranscriptionService(
          credentials,
          undefined,
          new WindowEventAudioService(),
          onProviderError,
        );
      }

      builtService = new TelemedicineTranscriptionService(
        transcriptionService,
        appointmentId,
        (_text, _event, _results, transcription) => setTranscription(transcription),
        setTranscriptionStatus,
      );
      return builtService;
    };

    return buildService().catch((err: any) => {
      const errorMessage = err?.message || 'Failed to setup transcription';
      setCredentialError(errorMessage);
      setTranscriptionStatus(TRANSCRIPTION_STATUS.error);
      MiscUtils.showToastrError('Transcription', errorMessage);
      MiscUtils.getBugsnagClient().notify(err);
      return null;
    });
  }, [appointmentId, isPhysician, hasTranscriptionPermission, retryCounter]);

  const [jitsi, setJitsi] = useState<IJitsiMeetExternalApi | null>(null);
  const [minimized, setMinimized] = useState(false);

  // Always points at the latest memoized service. The videoConferenceLeft
  // listener is registered once in onApiReady and its closure would otherwise
  // capture the service promise from THAT render — stale (usually null) when
  // consent is granted or credentials retried afterwards, leaving the real
  // service running forever (leaked Deepgram sessions reconnect indefinitely).
  const telemedicineTranscriptionServiceRef = useRef(telemedicineTranscriptionService);
  telemedicineTranscriptionServiceRef.current = telemedicineTranscriptionService;

  const retryCredentials = () => {
    setRetryCounter((prev) => prev + 1);
  };

  useEffect(() => {
    if (!jitsi || !hasTranscriptionPermission || !telemedicineTranscriptionService) {
      return undefined;
    }
    let cancelled = false;
    telemedicineTranscriptionService
      .then(async (it) => {
        if (!it) {
          return;
        }
        if (cancelled) {
          // Superseded (memo rebuilt / unmount) before start — make sure the
          // already-started session is torn down.
          it.stop().catch(() => {});
          return;
        }
        await it.start();
        if (cancelled) {
          // The cleanup ran while start() was in flight; it had nothing to
          // stop at the time, so tear down here.
          it.stop().catch(() => {});
        }
      })
      .catch((err: any) => {
        // A rejection caused by our own cleanup tearing down an in-flight
        // start is not a failure the NP should see.
        if (!cancelled) {
          MiscUtils.showToastrError(
            'Failed to Start transcription',
            err?.message ?? 'Unknown Error',
          );
        }
      });
    return () => {
      // Without this cleanup, a rebuilt service (consent flow, retry) or an
      // unmount orphans the previous session — it keeps recording with nothing
      // left holding a reference to stop it.
      cancelled = true;
      telemedicineTranscriptionService.then((it) => it?.stop().catch(() => {})).catch(() => {});
    };
  }, [jitsi, hasTranscriptionPermission, telemedicineTranscriptionService]);

  const displayName = isPhysician
    ? StaffUtils.getName(user as Physician)
    : (user as Patient)?.preferred_name ||
      PatientUtils.getName(user as Patient, meetingData.patient_name, false, false);

  let className = `jitsi-wrapper`;
  if (!isPhysician) className += ` full-screen`;
  else if (minimized) className += ' minimized';
  else className += ' normal-size';

  const videoRef = useRef();

  async function saveBlob(
    blob: Blob,
    metadata: { mime: string; extension: string; description: string },
  ) {
    let filename = `telemedicine ${CalendarUtils.format(moment().unix(), 'DD/MM/YYYY h:mm:ss A')}.${
      metadata.extension
    }`;
    let file = new File([blob], filename, {
      type: metadata.mime,
    });
    return dispatch(
      api.Documents.addDocument({
        file,
        title: metadata.description,
        //@ts-expect-error BUG?
        approved: true,
        documentType: DocumentUtils.DOCUMENT_TYPE.other,
        id: saveTo,
      }),
      {
        title: 'Screenshot',
        successText: 'Screenshot saved successfully!',
      },
    );
  }

  function onScreenCapture() {
    MiscUtils.playAudio(shutterSound);
    if (jitsi) {
      jitsi
        .captureLargeVideoScreenshot()
        .then((data) => {
          //@ts-expect-error
          const base64Response = data.dataURL.split(',')[1];
          const byteCharacters = atob(base64Response);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: RECORDING_METADATA.mime });
          return saveBlob(blob, RECORDING_METADATA);
        })
        .catch((e) => console.warn('Screenshot capture failed', e));
    }
  }

  return (
    <div className={className}>
      {isPhysician && (
        <div className="top-bar">
          <div className="size-buttons">
            <div
              className="minimize-button grow-hover round-button"
              onClick={() => setMinimized(!minimized)}
            >
              <Icon
                src={minimized ? 'window-maximize' : 'window-minimize'}
                className="white"
                alt={minimized ? 'Show' : 'Hide'}
                showTooltip={true}
              />
            </div>
            {!minimized && !!videoRef && (
              <div className="photo-button grow-hover round-button" onClick={onScreenCapture}>
                <Icon
                  height="15px"
                  src="camera"
                  className="white"
                  alt="Take Photo"
                  showTooltip={true}
                />
              </div>
            )}
            {isPhysician && canUseTranscription && !minimized && (
              <div
                className="transcription-button grow-hover round-button"
                data-transcription-status={transcriptionStatus}
                onClick={() => setShowTranscription((prev) => !prev)}
              >
                {transcriptionStatus === TRANSCRIPTION_STATUS.transcribing ? (
                  <EllipsisAnimatedIcon />
                ) : (
                  <Icon className="white" src="message" alt="Open Transcription" showTooltip />
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {isPhysician && (
        <TranscriptionPopup
          isOpen={showTranscription && !minimized}
          setOpen={setShowTranscription}
          hasTranscriptionPermission={hasTranscriptionPermission}
          setHasTranscriptionPermission={setHasTranscriptionPermission}
          transcription={transcription}
          appointmentId={appointmentId}
          credentialError={credentialError}
          onRetryCredentials={retryCredentials}
        />
      )}
      <JitsiMeeting
        ref={videoRef}
        domain="video2.careand.ca"
        roomName={meetingData.room_name!}
        configOverwrite={{
          startWithVideoMuted: !defaults.video,
          startWithAudioMuted: !defaults.audio,
        }}
        userInfo={{
          //@ts-expect-error
          displayName,
        }}
        getIFrameRef={(iframeRef) => {
          iframeRef.style.height = '100%';
        }}
        onApiReady={(jitsiApi) => {
          setJitsi(jitsiApi);
          jitsiApi.addListener('videoConferenceJoined', async () => {
            //@ts-expect-error
            if (onConnected) onConnected(jitsiApi._numberOfParticipants);
          });
          jitsiApi.addListener('videoConferenceLeft', async (ev) => {
            try {
              // Via the ref: this listener outlives the render that registered
              // it, and must stop the CURRENT service, not that render's.
              let service = await telemedicineTranscriptionServiceRef.current;
              service?.stop();
            } catch (err: any) {
              MiscUtils.showToastrError(
                'Failed to Stop transcription',
                err?.message ?? 'Unknown Error',
              );
            }
            onDisconnected();
          });
          // api.addListener('deviceListChanged', (devices) => {
          //   api.getCurrentDevices()
          //     .then(it => console.log('current devices', it))
          //   console.log('devices', devices)
          // });
          jitsiApi.executeCommand('setNoiseSuppressionEnabled', {
            enabled: true,
          });
        }}
      />
    </div>
  );
};

function TranscriptionPermission({ callback }: { callback: (hasPermission: boolean) => void }) {
  return (
    <div className="transcription-permission m-2 px-2 py-2">
      <div className="small-title">Transcription Consent</div>
      <div className="text-sm">Has the patient given consent to the transcription?</div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <DefaultButton
          name="No"
          className="red"
          onClick={() => callback(false)}
          style={{ minWidth: 'unset', width: 'unset' }}
        />
        <DefaultButton
          name="Yes"
          className="blue"
          onClick={() => callback(true)}
          style={{ minWidth: 'unset', width: 'unset' }}
        />
      </div>
      <div className="suggested-script">
        <div className="script-title">How to Ask for Consent</div>
        <div className="primary-script">
          "Would you be comfortable if I use an AI note-taking tool during our visit today?"
        </div>
        <div className="follow-up">
          <span className="question">If patient asks "What is that?": </span>
          "It's a tool that converts our conversation into medical notes so I can focus on you
          instead of typing."
        </div>
        <div className="follow-up">
          <span className="question">If patient asks about privacy: </span>
          "The audio recording is not stored, and transcriptions are safely stored as part of your
          health records."
        </div>
      </div>
    </div>
  );
}

export function TranscriptionPopup({
  isOpen,
  setOpen,
  hasTranscriptionPermission,
  setHasTranscriptionPermission,
  transcription,
  appointmentId,
  credentialError,
  onRetryCredentials,
}: {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  hasTranscriptionPermission: boolean;
  setHasTranscriptionPermission: (open: boolean) => void;
  setSoloDictation?: (open: boolean) => void;
  soloDictation?: boolean;
  transcription: AppointmentTranscriptionWithPartialIndicators | undefined;
  appointmentId?: string;
  credentialError?: string | null;
  onRetryCredentials?: () => void;
}) {
  const dispatch = useAppDispatch();
  const [transcriptionPlaceholder, setTranscriptionPlaceholder] =
    useState<AppointmentTranscription>();

  useEffect(() => {
    if (appointmentId) {
      dispatch(api.Appointments.getAppointmentTranscription({ id: appointmentId })).then(
        (response) => {
          if (response) {
            setTranscriptionPlaceholder(response);
          }
        },
      );
    }
  }, [dispatch, appointmentId]);

  return (
    <div className={`transcription-popup-view flex flex-col ${isOpen ? '' : 'is-closed'}`}>
      <div
        className="transcription-popup-view-header grid"
        style={{ gridTemplateColumns: '1fr 3fr 1fr' }}
      >
        <div></div>
        <div className="title text-center content-center font-bold">Transcription</div>
        <div className="grow-hover round-button" style={{ paddingTop: '5px' }}>
          <Icon
            src="window-minimize"
            className="white"
            alt="minimize transcription"
            showTooltip
            onClick={() => setOpen(false)}
          />
        </div>
      </div>
      <hr />
      <div className="transcription-scroll overflow-auto h-full">
        {hasTranscriptionPermission ? (
          <TranscriptionChat
            transcription={transcription || transcriptionPlaceholder}
            appointmentId={appointmentId}
            enableAutoBackup={!!transcription}
            enableManualSave={!!transcription}
            credentialError={credentialError}
            onRetryCredentials={onRetryCredentials}
          />
        ) : (
          <TranscriptionPermission
            callback={(permission) => {
              if (permission) {
                setHasTranscriptionPermission(true);
              } else {
                setHasTranscriptionPermission(false);
                setOpen(false);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}

// How often the auto-backup checkpoints the transcript. Each checkpoint is a
// complete, order-correct snapshot (the server's full-object overwrite IS the
// ordering guarantee), so this only bounds how often the growing transcript is
// re-sent, never how it is assembled. Byte-identical snapshots are deduped, so
// ticks during silence cost nothing; teardown flushes the final state, leaving a
// hard crash mid-interval as the only at-risk window.
const BACKUP_CHECKPOINT_INTERVAL_MS = 5000;

export function TranscriptionChat({
  transcription,
  style,
  appointmentId,
  enableAutoBackup = false,
  enableManualSave = false,
  credentialError,
  onRetryCredentials,
}: {
  transcription: AppointmentTranscriptionWithPartialIndicators | undefined;
  style?: React.CSSProperties;
  appointmentId?: string;
  enableAutoBackup?: boolean;
  enableManualSave?: boolean;
  credentialError?: string | null;
  onRetryCredentials?: () => void;
}) {
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const lastHeight = useRef<number>(-1);
  // Body of the last snapshot that actually committed; byte-identical snapshots
  // are skipped (silence / interim churn rebuilds the same document).
  const lastSavedPayload = useRef<string | null>(null);
  // Serializes saves so a slower, older/shorter in-flight snapshot can never
  // commit AFTER — and truncate — a newer/longer one. A client-side promise
  // chain, not a lock: it orders THIS session's single writer, which is the only
  // concurrency on this path.
  const backupQueue = useRef<Promise<void>>(Promise.resolve());
  // Saves enqueued but not yet settled. The generated fetch client has no
  // timeout, so a stalled request would otherwise let checkpoint ticks pile
  // stale snapshots onto the queue that all replay when it unblocks.
  const pendingBackups = useRef<number>(0);
  // Latest non-empty transcription, so the checkpoint/unmount flush always saves
  // the most recent state even if `transcription` is cleared right before unmount.
  const latestTranscription = useRef(transcription);
  if (transcription?.messages?.length) {
    latestTranscription.current = transcription;
  }
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'save-error'>('idle');
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [, forceUpdate] = useState({}); // For forcing re-renders to update "fromNow" text

  // Backup function — serialized and deduped. Every save is a complete snapshot
  // (the server PUT replaces the whole record; that idempotent overwrite is the
  // ordering guarantee). The queue commits saves in submission order, and dedup
  // drops bodies identical to the last committed save.
  const backupTranscription = React.useCallback(
    (
      transcriptionToBackup: AppointmentTranscriptionWithPartialIndicators,
      { isManual = false }: { isManual?: boolean } = {},
    ) => {
      if (!appointmentId) {
        MiscUtils.showToastrError('Transcription Backup', 'No appointment selected');
        return backupQueue.current;
      }

      if (!transcriptionToBackup.messages?.length) {
        MiscUtils.showToastrError('Transcription Backup', 'Nothing to save');
        return backupQueue.current;
      }

      const appointmentTranscription = {
        ...transcriptionToBackup,
        messages: (transcriptionToBackup.messages || []).map((m) => {
          return {
            ...m,
            isPartial: undefined,
          };
        }),
      };
      const payload = JSON.stringify(appointmentTranscription.messages);

      pendingBackups.current += 1;
      backupQueue.current = backupQueue.current
        .then(async () => {
          if (payload === lastSavedPayload.current) {
            // Already persisted this exact state — skip the network write, but
            // still confirm an explicit user save.
            if (isManual) {
              setSaveStatus('saved');
              setTimeout(() => setSaveStatus('idle'), 2000);
            }
            return;
          }

          setSaveStatus('saving');
          const { isSuccess } = await dispatch(
            api.Appointments.editOrAddAppointmentTranscription({
              id: appointmentId,
              appointmentTranscription,
            }),
            {
              meta: true,
              errorTitle: 'Transcription Backup',
              errorText: 'Could not save the transcription',
            },
          );
          if (!isSuccess) {
            // lastSavedPayload stays unchanged so the next checkpoint retries
            // this content.
            setSaveStatus('save-error');
            return;
          }
          lastSavedPayload.current = payload;
          setSaveStatus('saved');
          setLastSaveTime(new Date());
          setTimeout(() => setSaveStatus('idle'), 2000);
        })
        // Keep the chain alive so one failed save can't wedge every later one.
        .catch((err) => {
          console.log(err);
          setSaveStatus('save-error');
        })
        .finally(() => {
          pendingBackups.current -= 1;
        });

      return backupQueue.current;
    },
    [dispatch, appointmentId],
  );

  // Manual save function
  const handleManualSave = React.useCallback(() => {
    if (transcription) {
      backupTranscription(transcription, { isManual: true }).catch(console.log);
    }
  }, [transcription, backupTranscription]);

  // Auto-backup: checkpoint the latest transcript on a fixed interval. Dedup
  // makes quiet ticks free, so during speech this costs at most one snapshot
  // PUT per interval and none during silence — regardless of how fast segments
  // finalize.
  useEffect(() => {
    if (!enableAutoBackup || !appointmentId) {
      return;
    }
    const checkpoint = setInterval(() => {
      // One save running and one already queued behind it (stalled network) —
      // don't pile more snapshots onto the queue; the queued save plus the
      // first tick after it drains cover this content.
      if (pendingBackups.current >= 2) {
        return;
      }
      if (latestTranscription.current?.messages?.length) {
        backupTranscription(latestTranscription.current).catch(console.log);
      }
    }, BACKUP_CHECKPOINT_INTERVAL_MS);
    return () => clearInterval(checkpoint);
  }, [enableAutoBackup, appointmentId, backupTranscription]);

  // The unmount flush below runs from an empty-deps cleanup, so it must read
  // CURRENT values through a ref: the Voip/ClinicTranscription popups mount this
  // component before live transcription starts (enableAutoBackup still false,
  // appointmentId possibly unset), and a first-render closure would permanently
  // disable the final flush for the whole session.
  const flushContext = useRef({ enableAutoBackup, appointmentId, backupTranscription });
  flushContext.current = { enableAutoBackup, appointmentId, backupTranscription };

  // Final flush on unmount so the last <=interval of transcript is persisted when
  // the visit ends / the component tears down. It enqueues after any in-flight
  // checkpoint (in-order commit), and dedup makes it a no-op if that checkpoint
  // already persisted this exact state.
  useEffect(() => {
    return () => {
      const { enableAutoBackup, appointmentId, backupTranscription } = flushContext.current;
      const latest = latestTranscription.current;
      if (enableAutoBackup && appointmentId && latest?.messages?.length) {
        backupTranscription(latest).catch(console.log);
      }
    };
    // Unmount-only flush: deps intentionally empty so cleanup runs once, at
    // teardown; flushContext keeps the captured values fresh.
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = element;

    if (!transcription?.messages || transcription.messages.length === 0) {
      return;
    }
    if (lastHeight.current === scrollHeight) {
      return;
    }

    const scrollBottom = scrollHeight - scrollTop - clientHeight;
    if (scrollBottom < 150 || lastHeight.current === -1) {
      element.scrollTo({
        top: scrollHeight - clientHeight,
        behavior: 'smooth',
      });
    }
    lastHeight.current = scrollHeight;
  }, [transcription]);

  // Update "fromNow" text every 10 seconds when we have a lastSaveTime
  useEffect(() => {
    if (lastSaveTime) {
      const interval = setInterval(() => {
        forceUpdate({}); // Trigger re-render to update "fromNow" text
      }, 10000); // Update every 10 seconds

      return () => clearInterval(interval);
    }
  }, [lastSaveTime]);

  const getSaveStatusText = () => {
    if (!appointmentId) {
      return "Can't save: missing appointment ID";
    }

    switch (saveStatus) {
      case 'saving':
        return 'Saving...';
      case 'saved':
        return lastSaveTime ? `Saved ${moment(lastSaveTime).fromNow()}` : 'Saved successfully';
      case 'save-error':
        return 'Save failed';
      default:
        return lastSaveTime ? `Saved ${moment(lastSaveTime).fromNow()}` : 'Ready to save';
    }
  };

  return (
    <div className="transcription-chat-container w-full h-full flex flex-col" style={style}>
      {(enableAutoBackup || enableManualSave) && (
        <div className="transcription-header flex justify-between items-center p-2 border-b border-gray-200 bg-gray-50 text-xs">
          <div className="flex items-center gap-2">
            {enableAutoBackup && appointmentId && (
              <Icon
                src="sync-alt"
                alt="Auto refresh active"
                showTooltip={true}
                width="12px"
                height="12px"
              />
            )}
            <span className={`save-status ${appointmentId ? saveStatus : 'save-error'}`}>
              {getSaveStatusText()}
            </span>
          </div>
          {enableManualSave && appointmentId && (
            <DefaultChip
              className={saveStatus}
              onClick={handleManualSave}
              disabled={!transcription?.messages || saveStatus === 'saving'}
              text={saveStatus === 'saving' ? 'Saving...' : 'Save Now'}
            />
          )}
        </div>
      )}
      {credentialError && (
        <div className="transcription-error-banner flex flex-col gap-3 p-4 m-2 border-2 border-red-500 bg-red-50 rounded text-sm items-center">
          <div className="flex items-start gap-2">
            <Icon src="exclamation-triangle" className="text-red-600" width="16px" height="16px" />
            <div className="flex-1">
              <div className="font-semibold text-red-800">Transcription Service Unavailable</div>
              <div className="text-red-700 mt-1">{credentialError}</div>
            </div>
          </div>
          {onRetryCredentials && (
            <DefaultButton
              name="Retry Connection"
              className="blue"
              onClick={onRetryCredentials}
              style={{ minWidth: 'unset', width: 'fit-content' }}
            />
          )}
        </div>
      )}
      {!credentialError && (
        <div
          className="transcription-chat w-full overflow-auto flex flex-col p-4 flex-grow scroll-smooth"
          ref={ref}
        >
          {!transcription?.messages?.length && (
            <div className="consent-given">
              Transcription enabled. Conversation will appear here.
            </div>
          )}
          {_(transcription?.messages)
            .map((message, idx) => {
              let className = 'transcription-message';
              const isStaff = message.from === AppointmentTranscriptionMessageFromEnum.Staff;
              const style: CSSProperties = {
                alignSelf: isStaff ? 'end' : 'start',
                paddingInline: '8px',
                paddingBlock: '4px',
                borderRadius: '10px',
                color: isStaff ? 'var(--color-text-on-action)' : 'var(--color-text-primary)',
                backgroundColor: isStaff
                  ? 'var(--color-action-primary-bg)'
                  : 'var(--color-bg-surface)',
                marginTop: '8px',
                fontSize: '14px',
              };

              if (message!.isPartial) {
                className += ' partial';
              }

              return (
                <div key={idx} className={className} data-speaker={message.from} style={style}>
                  {message.text}
                </div>
              );
            })
            .value()}
        </div>
      )}
    </div>
  );
}

export const EllipsisAnimatedIcon = ({ dotRadius = 2 }: { dotRadius?: number }) => {
  const dotSize = 2 * dotRadius;
  const dotPadding = 2;
  const animationHeight = 4;
  const maxY = animationHeight + dotRadius;
  const minY = 0 + dotRadius;
  const width = 3 * dotSize + 2 * dotPadding;
  const height = dotSize + (maxY - minY);
  return (
    <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg">
      <style>{'.dot { fill: white; }'}</style>
      {_.range(3).map((idx) => {
        return (
          <circle
            key={idx}
            className="dot"
            cx={dotRadius + idx * (dotSize + dotPadding)}
            cy={maxY}
            r={dotRadius}
          >
            <animate
              attributeName="cy"
              values={`${maxY}; ${minY}; ${maxY}`}
              dur="1.5s"
              repeatCount="indefinite"
              begin={idx * 0.5 + 's'}
            />
          </circle>
        );
      })}
    </svg>
  );
};

export function useTranscriptionState({
  appointmentId,
  patientId,
  isPhysician,
}: {
  appointmentId: string | undefined;
  patientId: string;
  isPhysician: boolean;
}) {
  const dispatch = useAppDispatch();
  const canUseTranscription = !!appointmentId && isPhysician;
  const patient = useAppSelector((state) => state.patients.byId[patientId]);
  const [showTranscription, setShowTranscription] = useState(canUseTranscription);
  const [transcription, setTranscription] = useState<
    AppointmentTranscriptionWithPartialIndicators | undefined
  >();
  const [hasTranscriptionPermission, setHasTranscriptionPermission] = useState(
    (patient?.scribe_consent || false) && canUseTranscription,
  );
  const [soloDictation, setSoloDictation] = useState<boolean>(false);

  useEffect(() => {
    if (patient && !patient.scribe_consent && hasTranscriptionPermission) {
      let update = { ...patient, scribe_consent: true };
      dispatch(api.Patients.updatePatient({ id: patient.id!, patient: update }));
    }
  }, [dispatch, patient, hasTranscriptionPermission]);

  const [transcriptionStatus, setTranscriptionStatus] = useState<TRANSCRIPTION_STATUS>(
    TRANSCRIPTION_STATUS.inactive,
  );

  return {
    transcription,
    setTranscription,
    transcriptionStatus,
    setTranscriptionStatus,
    hasTranscriptionPermission: hasTranscriptionPermission || soloDictation,
    soloDictation,
    setHasTranscriptionPermission,
    setSoloDictation,
    showTranscription,
    setShowTranscription,
    canUseTranscription,
  };
}

export default JitsiMeet;
